import { useState, useEffect } from 'react';
import { useHTTP } from 'hooks/useRequest';
import LabelCounter from 'components/cef_form/label_counter';
import { requestErrors } from './approvals_group_text';
import { groupActions } from './approvals_group_reducer';

const ApprovalsGroupFormInputs = ({
  initialGroupName,
  nameInput,
  nameIsValid,
  nameErrMsg,
  nameErrRef,
  descriptionInput,
  approvalGroupDispatch,
  validateNameUrl,
  nameMaxLength,
  descriptionMaxLength,
}) => {
  const [sendValidation, setSendValidation] = useState(false);
  // Check name isValid using endpoint
  const requestConfig = {
    url: sendValidation ? `${validateNameUrl}?name=${nameInput}` : null,
    method: 'get',
  };
  const { error: httpError, data: httpData } = useHTTP(requestConfig);

  useEffect(() => {
    if (httpData) {
      // Update nameIsValid state to true when no error is returned from endpoint
      approvalGroupDispatch({
        type: groupActions.NAME_ISVALID,
        payload: requestErrors.uniqueName,
      });
      // Prevent repeat requests
      setSendValidation(false);
    }
  }, [httpData]);

  useEffect(() => {
    if (httpError === undefined) return;
    // Update nameIsValid state to false when an error is returned from endpoint
    approvalGroupDispatch({
      type: groupActions.NAME_INVALID,
      payload: requestErrors.nameAlreadyExists,
    });
    // Prevent repeat requests
    setSendValidation(false);
  }, [httpError]);

  const nameChangeHandler = event => {
    const groupName = event.target.value;
    approvalGroupDispatch({
      type: groupActions.NAME_INPUT,
      payload: groupName,
    });
    approvalGroupDispatch({ type: groupActions.FORM_UPDATED });
    // Prevent validation request when initial group name is input again
    if (groupName.trim() === initialGroupName) {
      // Validation state update as the initial group name is always valid
      approvalGroupDispatch({
        type: groupActions.NAME_ISVALID,
        payload: requestErrors.uniqueName,
      });
      return;
    }
    // Validate name on each keystroke if its invalid
    if (!nameIsValid && nameInput.length !== 0) setSendValidation(true);
  };

  const nameBlurHandler = () => {
    // Prevent validation request when initial group name is input again
    if (nameInput.trim() === initialGroupName) return;
    // Validate name when leaving input if the name is valid and not an empty string
    if (nameIsValid === true && nameInput.trim().length > 0) {
      setSendValidation(true);
    }
  };

  const descriptionChangeHandler = event => {
    const descriptionText = event.target.value;
    approvalGroupDispatch({
      type: groupActions.DESCRIPTION_INPUT,
      payload: descriptionText,
    });
    approvalGroupDispatch({ type: groupActions.FORM_UPDATED });
  };

  const inputErrorClassName = `ff-body fs-12 mb-18 ${
    nameIsValid ? 'tc-oa-grey' : 'tc-cef-red'
  }`;

  return (
    <>
      <LabelCounter
        id="group-name"
        label="Group Name"
        count={nameInput.length}
        limit={nameMaxLength}
        labelClassName="ff-body fw-500 fs-14 tc-grey-70 lh-1"
        countClassName="ff-body fs-12 tc-oa-grey lh-1 mb-0"
        containerClassName="approvals__label-counter mb-6"
      />
      <input
        type="text"
        id="group-name"
        name="group-name"
        className="create-group__text-input"
        value={nameInput}
        onChange={nameChangeHandler}
        onBlur={nameBlurHandler}
        maxLength={nameMaxLength}
      />
      <p className={`${inputErrorClassName} lh-1`} ref={nameErrRef}>
        {nameErrMsg}
      </p>
      <LabelCounter
        id="group-description"
        label={[
          'Group Description',
          <span key="gd-span" className="fw-400">
            {' '}
            (Optional)
          </span>,
        ]}
        count={descriptionInput.length}
        limit={descriptionMaxLength}
        labelClassName="ff-body fw-500 fs-14 tc-grey-70 lh-1"
        countClassName="ff-body fs-12 tc-oa-grey lh-1 mb-0"
        containerClassName="approvals__label-counter mb-6"
      />
      <textarea
        id="group-description"
        name="group-description"
        className="create-group__textarea-input mb-5"
        value={descriptionInput}
        onChange={descriptionChangeHandler}
        maxLength={descriptionMaxLength}
      />
      <p className="ff-body fs-12 tc-oa-grey lh-1 mb-28">
        Provide a short description to identify this group
      </p>
    </>
  );
};

export default ApprovalsGroupFormInputs;
